import type { PopoverProps } from '@byecode/ui'
import { AutoResizeDropdown, Box, Flex, Input, Popover, Text } from '@byecode/ui'
import type { NodeGroupOption, NodeOption, SenderAccountType, WorkflowType } from '@lighthouse/shared'
import { type NodeTypes, useAtomData } from '@lighthouse/shared'
import React, { useEffect, useMemo, useState } from 'react'

import { otherPlatformListAtom } from '@/atoms/application/state'

import { nodeList, unitList } from './constants'
import SelectItem from './SelectItem'
import * as SC from './styles'
import { filterList } from './util'

interface NodeSelectorProps extends Omit<PopoverProps, 'opened' | 'children'> {
    opened: boolean
    onSelect: (value: { val: NodeTypes; senderEmailType?: SenderAccountType }) => void
    target: HTMLDivElement | null
    workflowType?: WorkflowType
}

const nodeActive = {
    border: 'none',
    background: '#fff',
    borderRight: '1px solid var(--color-gray-200)',
    color: 'var(--color-gray-900)'
}

const unitActive = {
    border: 'none',
    background: '#fff',
    borderLeft: '1px solid var(--color-gray-200)',
    color: 'var(--color-gray-900)'
}

const tabs = [
    { id: 0, iconType: 'NodeTree', label: '节点' },
    { id: 1, iconType: 'Tool', label: '工具' }
]

export const NodeSelector: React.FC<NodeSelectorProps> = ({ opened, onSelect, target, position = 'right', workflowType, ...rest }) => {
    // const [opened, setOpened] = useState(false)
    const otherPlatforms = useAtomData(otherPlatformListAtom)
    // const [isEmpty, setIsEmpty] = useState(false)

    const [tab, setTab] = useState(0)

    const [searchValue, setSearchValue] = useState('')

    const handleToggle = (val: number) => {
        setTab(val)
    }

    const filteredUnitList = useMemo(() => {
        const platforms: string[] = otherPlatforms?.map(item => item.type) ?? []
        return unitList.filter(item => item.unitType && platforms.includes(item.unitType))
    }, [otherPlatforms])

    const handleClickNode = (node: NodeOption) => {
        onSelect({ val: node.value, senderEmailType: node?.subType })
    }

    const searchList = useMemo(() => {
        const list = tab === 0 ? nodeList : filteredUnitList
        return filterList(list, searchValue, workflowType)
    }, [filteredUnitList, searchValue, tab, workflowType])

    const isEmpty = useMemo(() => searchList.every(item => !item.children || item.children.length === 0), [searchList])

    useEffect(() => {
        if (opened) {
            setTab(0)
            setSearchValue('')
        }
    }, [opened])

    return (
        <Popover width={320} opened={opened} position={position} withinPortal {...rest}>
            <Popover.AnchorEl anchorEl={target} />
            <AutoResizeDropdown compact initMaxHeight={810}>
                <SC.Container>
                    <SC.Tabs>
                        {tabs.map(tabInfo => (
                            <SC.TabPane
                                key={tabInfo.id}
                                style={tab === tabInfo.id ? (tabInfo.id === 0 ? nodeActive : unitActive) : {}}
                                onClick={() => handleToggle(tabInfo.id)}
                            >
                                <SC.Icon
                                    size={16}
                                    type={tabInfo.iconType}
                                    color={tab === tabInfo.id ? 'var(--color-purple-500)' : 'var(--color-gray-400)'}
                                />
                                <SC.Tab>{tabInfo.label}</SC.Tab>
                            </SC.TabPane>
                        ))}
                    </SC.Tabs>
                    <Box
                        style={{
                            padding: '4px 12px 0',
                            width: '100%',
                            // position: 'sticky',
                            top: 0,
                            backgroundColor: 'var(--color-white)',
                            marginTop: '8px'
                        }}
                    >
                        <Input value={searchValue} onChange={e => setSearchValue(e.target.value)} />
                    </Box>
                    {isEmpty ? (
                        <Flex style={{ minHeight: 200 }} justifyContent="center" alignItems="center">
                            暂无数据
                        </Flex>
                    ) : (
                        <SC.List>
                            {searchList.map((parentItem: NodeGroupOption) => (
                                <SC.Item key={parentItem.label}>
                                    <Text pl={16} lineHeight="24px" mt={8} size={12} color="var(--color-gray-500)">
                                        {parentItem.label}
                                    </Text>
                                    {parentItem.children?.map((childItem: NodeOption) => (
                                        <SelectItem
                                            item={childItem}
                                            key={childItem.value}
                                            onClick={e => {
                                                e.stopPropagation()
                                                handleClickNode(childItem)
                                            }}
                                        />
                                    ))}
                                </SC.Item>
                            ))}
                        </SC.List>
                    )}
                </SC.Container>
            </AutoResizeDropdown>
        </Popover>
    )
}
